// src/pages/skills/SkillForm.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addSkill,
  getSkillById,
  updateSkill,
} from "../../services/skillService";

const SkillForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: "",
    category: "",
    level: "Beginner",
    status: "Planned",
    targetDate: "",
  });

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      getSkillById(id)
        .then((data) => setForm(data))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await updateSkill({ ...form, id });
      } else {
        await addSkill(form);
      }
      navigate("/skills");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading skill…</div>;
  }

  return (
    <div className="p-0 md:p-6 max-w-xl mx-auto pb-24">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">
          {isEdit ? "Edit Skill" : "Add Skill"}
        </h1>
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-gray-600 hover:underline"
        >
          Cancel
        </button>
      </div>

      {/* FORM CARD */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-sm border p-5 space-y-4"
      >
        {/* Skill Name */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Skill Name
          </label>
          <input
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            placeholder="e.g. React Native"
            className="w-full px-3 py-2 border rounded focus:ring focus:ring-blue-200"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Category
          </label>
          <input
            name="category"
            value={form.category}
            onChange={handleChange}
            placeholder="Frontend, Backend, Mobile…"
            className="w-full px-3 py-2 border rounded"
          />
        </div>

        {/* Level */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Skill Level
          </label>
          <select
            name="level"
            value={form.level}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded"
          >
            <option>Beginner</option>
            <option>Intermediate</option>
            <option>Advanced</option>
          </select>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Learning Status
          </label>
          <select
            name="status"
            value={form.status}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded"
          >
            <option>Planned</option>
            <option>In Progress</option>
            <option>Completed</option>
          </select>
        </div>

        {/* Target Date */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Target Date
          </label>
          <input
            type="date"
            name="targetDate"
            value={form.targetDate || ""}
            onChange={handleChange}
            className="w-full px-3 py-2 border rounded"
          />
        </div>

        {/* ACTIONS */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
          >
            {saving
              ? "Saving..."
              : isEdit
              ? "Update Skill"
              : "Add Skill"}
          </button>

          <button
            type="button"
            onClick={() => navigate("/skills")}
            className="flex-1 border py-2 rounded-lg hover:bg-gray-50"
          >
            Back
          </button>
        </div>
      </form>
    </div>
  );
};

export default SkillForm;
